import React, { useCallback, useMemo, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import cx from 'classnames';
import toast from 'react-hot-toast';
import Autolinker from 'autolinker';
import { ArrowLeft, FileText, Pencil, Plus, User } from 'lucide-react';
import {
  useFetchIncidentQuery,
  usePatchIncidentMutation,
} from '@@/services/incident';
import { useFetchPropertyQuery } from '@@/services/property';
import { useGetMeQuery } from '@@/services/user';
import {
  IncidentStatuses,
  translateIncidentStatus,
} from '@@/constants/incident';
import {
  openAddIncidentCommentModal,
  openAddIncidentInternalNoteModal,
  openEditIncidentTypeModal,
} from '@@/features/modal';
import AddIncidentCommentModal from '@@/components/Modals/AddIncidentCommentModal';
import AddIncidentInternalNoteModal from '@@/components/Modals/AddIncidentInternalNoteModal';
import EditIncidentTypeModal from '@@/components/Modals/EditIncidentTypeModal';
import { embedPath, isEmbedded } from '@@/utils/embed';

const Comment: React.FC<{ comment: Alacaza.IncidentComment }> = ({
  comment,
}) => {
  const createdAt = useMemo(
    () => moment(comment?.createdAt),
    [comment?.createdAt],
  );
  const { data: me } = useGetMeQuery();

  const isPartnerComment = me?.partner?.id === comment.creator.id;

  const html = Autolinker.link(comment.body, {
    className: 'text-primary-600 underline hover:text-primary-700',
    truncate: { length: 50, location: 'smart' },
  });

  return (
    <div className="my-3">
      <div
        className={cx(
          'rounded-lg p-4 break-words',
          comment.isInternal
            ? 'bg-warning-50 border-l-4 border-warning-400'
            : 'bg-neutral-50 border border-neutral-200',
        )}
        dangerouslySetInnerHTML={{ __html: html }}
      />

      <p className="text-neutral-500 text-body-xs mt-2 flex items-center gap-1">
        <User size={12} />
        le {createdAt.format('LL')} à {createdAt.format('LT')} par
        {/* La fiche utilisateur sort du périmètre incidents : en embed on
            n'affiche que le nom. */}
        {isPartnerComment || isEmbedded() ? (
          <span className="font-medium text-neutral-700">
            {comment.creator.displayedName}
          </span>
        ) : (
          <Link
            className="font-medium text-primary-600 hover:text-primary-700 underline"
            to={`/users/${comment.creator.id}`}
          >
            {comment.creator.displayedName}
          </Link>
        )}
      </p>
    </div>
  );
};

const IncidentPage = () => {
  const dispatch = useDispatch();
  const { incidentId } = useParams();
  const {
    data: incident,
    isLoading: incidentIsLoading,
    error: incidentError,
  } = useFetchIncidentQuery(incidentId || '', {
    skip: !incidentId,
  });
  const {
    data: property,
    isLoading: propertyIsLoading,
    error: propertyError,
  } = useFetchPropertyQuery(incident?.propertyId || '', {
    skip: !incident?.propertyId,
  });

  const [patchIncident, { isSuccess: resolveIsSuccess }] =
    usePatchIncidentMutation();

  const toggleIncidentStatus = useCallback(() => {
    if (!incident) {
      return;
    }

    if (
      window.confirm(
        incident.status === IncidentStatuses.IN_PROGRESS
          ? 'Etes-vous sûr de vouloir clôturer cet incident ?'
          : 'Etes-vous sûr de vouloir réouvrir cet incident ?',
      )
    ) {
      patchIncident({
        incidentId: incident.id,
        status:
          incident.status === IncidentStatuses.IN_PROGRESS
            ? IncidentStatuses.RESOLVED
            : IncidentStatuses.IN_PROGRESS,
      });
    }
  }, [patchIncident, incident]);

  const showAddIncidentCommentModal = useCallback(() => {
    dispatch(openAddIncidentCommentModal());
  }, [dispatch]);

  const showAddIncidentInternalNoteModal = useCallback(() => {
    dispatch(openAddIncidentInternalNoteModal());
  }, [dispatch]);

  const showEditIncidentTypeModal = useCallback(() => {
    dispatch(openEditIncidentTypeModal());
  }, [dispatch]);

  useEffect(() => {
    if (resolveIsSuccess) {
      toast.success("Le statut de l'incident a bien été modifié", {
        position: 'top-right',
      });
    }
  }, [resolveIsSuccess]);

  const createdAt = useMemo(
    () => moment(incident?.createdAt),
    [incident?.createdAt],
  );

  const date = useMemo(() => moment(incident?.date), [incident?.date]);

  // `null` tant que l'incident n'a jamais été clôturé — et pour l'historique
  // antérieur au champ `closedAt` que le backfill n'aurait pas couvert.
  const closedAt = useMemo(
    () => (incident?.closedAt ? moment(incident.closedAt) : null),
    [incident?.closedAt],
  );

  const comments = useMemo(
    () => incident?.comments?.filter((c) => !c.isInternal) || [],
    [incident?.comments],
  );

  const internalNotes = useMemo(
    () => incident?.comments?.filter((c) => c.isInternal) || [],
    [incident?.comments],
  );

  // La requête résidence est `skip` tant que l'incident n'est pas là : son
  // `isLoading` reste faux, d'où le troisième terme — sinon un rendu « incident
  // introuvable » clignote entre les deux requêtes.
  const isLoading =
    incidentIsLoading ||
    propertyIsLoading ||
    (!!incident?.propertyId && !property && !propertyError);

  // Rendre `null` ferait remonter une hauteur nulle au parent en mode embed :
  // ces états occupent la place et disent ce qui se passe.
  if (isLoading) {
    return (
      <div className="px-page-sm py-6 lg:px-page lg:py-8 w-full">
        <div className="max-w-7xl mx-auto bg-white rounded-card shadow-card border border-neutral-200 p-8 text-center">
          <p className="text-body text-neutral-500">Chargement de l'incident…</p>
        </div>
      </div>
    );
  }

  if (incidentError || propertyError) {
    return (
      <div className="px-page-sm py-6 lg:px-page lg:py-8 w-full">
        <div className="max-w-7xl mx-auto bg-white rounded-card shadow-card border border-danger-200 p-8 text-center">
          <p className="text-body text-danger-600">
            Impossible de charger cet incident. Réessayez dans un instant.
          </p>
        </div>
      </div>
    );
  }

  if (!incident || !property) {
    return (
      <div className="px-page-sm py-6 lg:px-page lg:py-8 w-full">
        <div className="max-w-7xl mx-auto bg-white rounded-card shadow-card border border-neutral-200 p-8 text-center">
          <p className="text-body text-neutral-500">Incident introuvable</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {incidentId && <AddIncidentCommentModal incidentId={incidentId} />}
      {incidentId && <AddIncidentInternalNoteModal incidentId={incidentId} />}
      {incidentId && <EditIncidentTypeModal incidentId={incidentId} currentType={incident.type} />}
      {/* min-h-screen ferait déborder l'iframe : la hauteur y est pilotée par
          EmbedRoute via postMessage. */}
      <div className={cx('bg-neutral-50', !isEmbedded() && 'min-h-screen')}>
        <div className="px-page-sm py-6 lg:px-page lg:py-8 w-full">
          <div className="max-w-7xl mx-auto">
            {/* En embed il n'y a ni sidebar ni barre d'adresse : sans ce lien
                la vue détail est un cul-de-sac. */}
            <Link
              to={embedPath('/incidents')}
              className="inline-flex items-center px-4 py-2 mb-6 text-body-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-button hover:bg-neutral-50 hover:text-neutral-900 transition-colors duration-200 shadow-sm"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Retour aux incidents
            </Link>

            {/* Header */}
            <div className="bg-white rounded-card shadow-card border border-neutral-200 p-6 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <div
                    className={cx(
                      'inline-flex items-center px-3 py-1.5 rounded-badge text-body font-semibold mb-3',
                      incident.status === IncidentStatuses.RESOLVED
                        ? 'bg-success-500 text-white'
                        : 'bg-warning-500 text-neutral-900',
                    )}
                  >
                    Incident #{incidentId}
                  </div>
                  <p className="text-body-sm text-neutral-600">
                    Incident créé le {createdAt.format('LL')} à&nbsp;
                    {createdAt.format('LT')}
                    &nbsp;par&nbsp;
                    {isEmbedded() ? (
                      <span className="font-medium text-neutral-900">
                        {incident.creator.displayedName}
                      </span>
                    ) : (
                      <Link
                        className="text-primary-600 hover:text-primary-700 underline font-medium"
                        to={`/users/${incident.creator.id}`}
                      >
                        {incident.creator.displayedName}
                      </Link>
                    )}
                  </p>
                </div>

                <div className="flex flex-col items-start sm:items-end gap-2">
                  <button
                    className={cx(
                      'inline-flex items-center px-6 py-3 rounded-button text-white font-medium shadow-button transition-colors duration-200',
                      incident.status === IncidentStatuses.IN_PROGRESS
                        ? 'bg-danger-600 hover:bg-danger-700'
                        : 'bg-warning-500 hover:bg-warning-600',
                    )}
                    onClick={toggleIncidentStatus}
                  >
                    {incident.status === IncidentStatuses.IN_PROGRESS
                      ? "Clôturer l'incident"
                      : "Réouvrir l'incident"}
                  </button>

                  {incident.status === IncidentStatuses.RESOLVED && closedAt && (
                    <p className="text-body-sm text-neutral-600">
                      Incident clôturé le {closedAt.format('LL')} à&nbsp;
                      {closedAt.format('LT')}.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Panel - Incident Details */}
              <div className="bg-white rounded-card shadow-card border border-neutral-200 p-6 space-y-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <p className="text-body-sm font-medium text-neutral-500 uppercase tracking-wide">Type</p>
                    <button
                      onClick={showEditIncidentTypeModal}
                      className="text-neutral-400 hover:text-primary-600 transition-colors"
                      title="Modifier le type"
                    >
                      <Pencil size={14} />
                    </button>
                  </div>
                  <p className="text-body text-neutral-900 break-words">{incident.title}</p>
                </div>

                <div>
                  <p className="text-body-sm font-medium text-neutral-500 uppercase tracking-wide mb-2">Description</p>
                  <p className="text-body text-neutral-900 break-words">{incident.description}</p>
                </div>

                <div>
                  <p className="text-body-sm font-medium text-neutral-500 uppercase tracking-wide mb-2">Résidence</p>
                  <p className="text-body text-neutral-900">{property.name}</p>
                </div>

                {incident.creator?.buildingName && (
                  <div>
                    <p className="text-body-sm font-medium text-neutral-500 uppercase tracking-wide mb-2">Bâtiment</p>
                    <p className="text-body text-neutral-900">{incident.creator.buildingName}</p>
                  </div>
                )}

                <div>
                  <p className="text-body-sm font-medium text-neutral-500 uppercase tracking-wide mb-2">Localisation</p>
                  <p className="text-body text-neutral-900 break-words">{incident.area}</p>
                </div>

                <div>
                  <p className="text-body-sm font-medium text-neutral-500 uppercase tracking-wide mb-2">Date de l'incident</p>
                  <p className="text-body text-neutral-900">
                    {date.format('LL')} à {date.format('LT')}
                  </p>
                </div>

                <div>
                  <p className="text-body-sm font-medium text-neutral-500 uppercase tracking-wide mb-2">Statut</p>
                  <p className="text-body text-neutral-900">{translateIncidentStatus(incident.status)}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-body-sm font-medium text-neutral-500 uppercase tracking-wide mb-2">
                      Parties communes ?
                    </p>
                    <p className="text-body text-neutral-900">
                      {incident.involveCommonPortions ? 'OUI' : 'NON'}
                    </p>
                  </div>

                  <div>
                    <p className="text-body-sm font-medium text-neutral-500 uppercase tracking-wide mb-2">Privé ?</p>
                    <p className="text-body text-neutral-900">{incident.isPrivate ? 'OUI' : 'NON'}</p>
                  </div>
                </div>

                <div>
                  <p className="text-body-sm font-medium text-neutral-500 uppercase tracking-wide mb-3">Documents</p>
                  {incident.documents?.length > 0 ? (
                    <div className="space-y-2">
                      {/* <Link to={urlAbsolue}> ne gère les URLs absolues qu'à
                          partir de react-router 6.14 : ici c'est une vraie
                          ancre vers S3. */}
                      {incident.documents.map((document, i) => (
                        <a
                          key={i}
                          href={document.url}
                          className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 text-body-sm"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <FileText size={18} />
                          <span className="break-words">{document.name}</span>
                        </a>
                      ))}
                    </div>
                  ) : (
                    <p className="text-body-sm text-neutral-500">Aucun document n'a encore été ajouté</p>
                  )}
                </div>
              </div>

              {/* Right Panel - Comments and Notes */}
              <div className="space-y-6">
                {/* Discussion */}
                <div className="bg-white rounded-card shadow-card border border-neutral-200">
                  <div className="p-4 border-b border-neutral-200 flex items-center justify-between">
                    <h3 className="text-heading-sm text-neutral-900">Discussion</h3>
                    <button
                      onClick={showAddIncidentCommentModal}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-button shadow-button transition-colors duration-200"
                    >
                      <Plus size={18} />
                      <span className="text-body-sm">Ajouter</span>
                    </button>
                  </div>
                  <div className="p-4 h-[300px] overflow-y-auto">
                    {comments.length > 0 ? (
                      comments.map((comment, i) => (
                        <Comment key={i} comment={comment} />
                      ))
                    ) : (
                      <p className="text-body-sm text-neutral-500 text-center py-8">
                        Aucun commentaire n'a encore été publié
                      </p>
                    )}
                  </div>
                </div>

                {/* Internal Notes */}
                <div className="bg-white rounded-card shadow-card border border-neutral-200">
                  <div className="p-4 border-b border-neutral-200 flex items-center justify-between">
                    <h3 className="text-heading-sm text-neutral-900">Notes internes</h3>
                    <button
                      onClick={showAddIncidentInternalNoteModal}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-button shadow-button transition-colors duration-200"
                    >
                      <Plus size={18} />
                      <span className="text-body-sm">Ajouter</span>
                    </button>
                  </div>
                  <div className="p-4 h-[300px] overflow-y-auto">
                    {internalNotes.length > 0 ? (
                      internalNotes.map((note, i) => (
                        <Comment key={i} comment={note} />
                      ))
                    ) : (
                      <p className="text-body-sm text-neutral-500 text-center py-8">
                        Aucune note interne n'a encore été publiée
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default IncidentPage;
